import {
  Download,
  Person,
  Schedule,
  Assessment,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material'

import type { AIQCResponse } from "../../api"

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return "Invalid date"
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return "Invalid date"
  }
}

type LabReportData = {
  keyTakeaways?: string[]
  labPanels?: Array<{
    panelName: string
    description?: string
    values?: Array<{
      parameter: string
      userValue: string
      referenceRange?: string
      status: string
    }>
  }>
  supplements?: Array<{
    name: string
    dosage?: string
    timing?: string
    justification?: string
  }>
  lifestyle?: string[]
  followUp?: {
    labsToRepeat?: Array<{
      name: string
      when?: string
      why?: string
    }>
    additionalTests?: Array<{
      name: string
      reason?: string
    }>
    specialistReferrals?: Array<{
      type: string
      reason?: string
    }>
  }
}

const parseRecommendations = (recommendations: unknown): {
  summary?: string
  findings?: string[]
  recommendations?: string[]
  urgency?: 'low' | 'medium' | 'high'
  followUp?: string
  labReport?: LabReportData
} => {
  if (typeof recommendations === 'string') {
    try {
      const parsed = JSON.parse(recommendations)
      return parseRecommendations(parsed)
    } catch {
      return { summary: recommendations }
    }
  }

  if (typeof recommendations === 'object' && recommendations !== null) {
    const obj = recommendations as Record<string, unknown>

    // Check if this is a lab report structure
    if (obj.key_takeaways || obj.lab_panels || obj.personalized_recommendations) {
      const labReport: LabReportData = {}

      if (Array.isArray(obj.key_takeaways)) {
        labReport.keyTakeaways = obj.key_takeaways.filter((item): item is string => typeof item === 'string')
      }

      if (Array.isArray(obj.lab_panels)) {
        labReport.labPanels = obj.lab_panels.map((panel: Record<string, unknown>) => {
          const labPanel: { panelName: string; description?: string; values?: Array<{ parameter: string; userValue: string; referenceRange?: string; status: string }> } = {
            panelName: (panel.panel_name as string) || 'Lab Panel'
          }

          if (panel.description && typeof panel.description === 'string') {
            labPanel.description = panel.description
          }

          if (Array.isArray(panel.values)) {
            labPanel.values = panel.values.map((value: Record<string, unknown>) => {
              const labValue: { parameter: string; userValue: string; referenceRange?: string; status: string } = {
                parameter: (value.parameter as string) || '',
                userValue: (value.user_value as string) || '',
                status: (value.status as string) || ''
              }

              if (value.reference_range && typeof value.reference_range === 'string') {
                labValue.referenceRange = value.reference_range
              }

              return labValue
            })
          }

          return labPanel
        })
      }

      if (obj.personalized_recommendations && typeof obj.personalized_recommendations === 'object') {
        const recs = obj.personalized_recommendations as Record<string, unknown>
        if (Array.isArray(recs.supplements)) {
          labReport.supplements = recs.supplements.map((supp: Record<string, unknown>) => {
            const supplement: { name: string; dosage?: string; timing?: string; justification?: string } = {
              name: (supp.name as string) || ''
            }

            if (supp.dosage && typeof supp.dosage === 'string') {
              supplement.dosage = supp.dosage
            }

            if (supp.timing && typeof supp.timing === 'string') {
              supplement.timing = supp.timing
            }

            if (supp.justification && typeof supp.justification === 'string') {
              supplement.justification = supp.justification
            }

            return supplement
          })
        }
        if (Array.isArray(recs.lifestyle)) {
          labReport.lifestyle = recs.lifestyle.filter((item): item is string => typeof item === 'string')
        }
      }

      if (obj.follow_up && typeof obj.follow_up === 'object') {
        const followUp = obj.follow_up as Record<string, unknown>
        labReport.followUp = {}

        if (Array.isArray(followUp.labs_to_repeat)) {
          labReport.followUp.labsToRepeat = followUp.labs_to_repeat.map((lab: Record<string, unknown>) => {
            const labToRepeat: { name: string; when?: string; why?: string } = {
              name: (lab.name as string) || ''
            }

            if (lab.when && typeof lab.when === 'string') {
              labToRepeat.when = lab.when
            }

            if (lab.why && typeof lab.why === 'string') {
              labToRepeat.why = lab.why
            }

            return labToRepeat
          })
        }

        if (Array.isArray(followUp.additional_tests)) {
          labReport.followUp.additionalTests = followUp.additional_tests.map((test: Record<string, unknown>) => {
            const additionalTest: { name: string; reason?: string } = {
              name: (test.name as string) || ''
            }

            if (test.reason && typeof test.reason === 'string') {
              additionalTest.reason = test.reason
            }

            return additionalTest
          })
        }

        if (Array.isArray(followUp.specialist_referrals)) {
          labReport.followUp.specialistReferrals = followUp.specialist_referrals.map((ref: Record<string, unknown>) => {
            const specialistReferral: { type: string; reason?: string } = {
              type: (ref.type as string) || ''
            }

            if (ref.reason && typeof ref.reason === 'string') {
              specialistReferral.reason = ref.reason
            }

            return specialistReferral
          })
        }
      }

      return { labReport }
    }

    // Fallback to original parsing for other structures
    const result: {
      summary?: string
      findings?: string[]
      recommendations?: string[]
      urgency?: 'low' | 'medium' | 'high'
      followUp?: string
    } = {}

    if (typeof obj.summary === 'string') {
      result.summary = obj.summary
    }
    if (Array.isArray(obj.findings)) {
      const validFindings = obj.findings.filter((f): f is string => typeof f === 'string')
      if (validFindings.length > 0) {
        result.findings = validFindings
      }
    }
    if (Array.isArray(obj.recommendations)) {
      const validRecs = obj.recommendations.filter((r): r is string => typeof r === 'string')
      if (validRecs.length > 0) {
        result.recommendations = validRecs
      }
    }
    if (typeof obj.urgency === 'string' && ['low', 'medium', 'high'].includes(obj.urgency)) {
      result.urgency = obj.urgency as 'low' | 'medium' | 'high'
    }
    if (typeof obj.followUp === 'string') {
      result.followUp = obj.followUp
    }

    return result
  }

  return {}
}

const getUrgencyColor = (urgency?: string) => {
  switch (urgency) {
    case 'high': return 'error'
    case 'medium': return 'warning'
    case 'low': return 'success'
    default: return 'default'
  }
}

const getUrgencyIcon = (urgency?: string) => {
  switch (urgency) {
    case 'high': return <Warning />
    case 'medium': return <Info />
    case 'low': return <CheckCircle />
    default: return <Assessment />
  }
}

interface EnhancedReportDisplayProps {
  recommendations: AIQCResponse | null
  loading?: boolean
  patientName?: string
  onExport?: () => void
}

export function EnhancedReportDisplay({
  recommendations,
  loading = false,
  patientName = "Patient",
  onExport
}: EnhancedReportDisplayProps) {
  if (loading) {
    return (
      <Card>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Loading lab report...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (!recommendations) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="info">
            No lab report data available.
          </Alert>
        </CardContent>
      </Card>
    )
  }

  if (!recommendations.hasRecommendations) {
    return (
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="info">
            {recommendations.message || "No lab report data available."}
          </Alert>
        </CardContent>
      </Card>
    )
  }

  const parsedData = parseRecommendations(recommendations.recommendations)
  const hasStructuredData = parsedData.summary || parsedData.findings || parsedData.recommendations || parsedData.followUp || parsedData.labReport

  return (
    <Card elevation={2}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Person />
            <Typography variant="h5">
              Lab Report - {patientName}
            </Typography>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', gap: 1, displayPrint: 'none' }}>
            {parsedData.urgency && (
              <Chip
                icon={getUrgencyIcon(parsedData.urgency)}
                label={`${parsedData.urgency.toUpperCase()} PRIORITY`}
                color={getUrgencyColor(parsedData.urgency) as 'error' | 'warning' | 'success' | 'default'}
                variant="outlined"
              />
            )}
            {onExport && (
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={onExport}
                size="small"
              >
                Export PDF
              </Button>
            )}
          </Box>
        }
        sx={{ pb: 1 }}
      />

      <CardContent sx={{ pt: 0 }}>
        <Grid container spacing={3}>
          {/* Medical Disclaimer - print/PDF only */}
          <Grid size={{ xs: 12 }} sx={{ display: 'none', displayPrint: 'block' }}>
            <Paper sx={{ p: 2, border: 1, borderColor: 'grey.200' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                The report is for informational purposes only. Always consult with a qualified
                healthcare professional regarding lab results and before making any health decisions.
              </Typography>
            </Paper>
          </Grid>

          {/* Report Summary */}
          {parsedData.summary && (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2, bgcolor: 'primary.50', border: 1, borderColor: 'grey.200' }}>
                <Typography variant="h6" gutterBottom color="primary.main">
                  Clinical Summary
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {parsedData.summary}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Key Findings */}
          {parsedData.findings && parsedData.findings.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'grey.200' }}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Key Findings
                </Typography>
                <List dense>
                  {parsedData.findings.map((finding, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Assessment color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            {finding}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Recommendations */}
          {parsedData.recommendations && parsedData.recommendations.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'grey.200' }}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Clinical Recommendations
                </Typography>
                <List dense>
                  {parsedData.recommendations.map((rec, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            {rec}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Follow-up */}
          {parsedData.followUp && (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2, bgcolor: 'warning.50', border: 1, borderColor: 'grey.200' }}>
                <Typography variant="h6" gutterBottom color="warning.main">
                  Follow-up Instructions
                </Typography>
                <Typography variant="body1">
                  {parsedData.followUp}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Lab Report Sections */}
          {parsedData.labReport && (
            <>
              {/* Key Takeaways */}
              {parsedData.labReport.keyTakeaways && parsedData.labReport.keyTakeaways.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 3, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle />
                      Key Takeaways
                    </Typography>
                    <List dense>
                      {parsedData.labReport.keyTakeaways.map((takeaway, index) => (
                        <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <Assessment color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                {takeaway}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              )}

              {/* Lab Panels */}
              {parsedData.labReport.labPanels && parsedData.labReport.labPanels.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom color="text.primary" sx={{ mt: 2, mb: 2 }}>
                    Lab Analysis
                  </Typography>
                  {parsedData.labReport.labPanels.map((panel, index) => (
                    <Paper key={index} sx={{ p: 3, mb: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
                      <Typography variant="h6" gutterBottom color="primary.main">
                        {panel.panelName}
                      </Typography>
                      {panel.description && (
                        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6, color: 'text.secondary' }}>
                          {panel.description}
                        </Typography>
                      )}
                      {panel.values && panel.values.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          {panel.values.map((value, valueIndex) => (
                            <Box key={valueIndex} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {value.parameter}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={value.status}
                                  color={
                                    value.status.toLowerCase().includes('supportive') || value.status.toLowerCase().includes('optimal') ? 'success' :
                                    value.status.toLowerCase().includes('lower') || value.status.toLowerCase().includes('upper') ? 'warning' :
                                    'default'
                                  }
                                  variant="outlined"
                                />
                              </Box>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Your Value:</strong> {value.userValue}
                              </Typography>
                              {value.referenceRange && (
                                <Typography variant="caption" color="text.secondary">
                                  Reference Range: {value.referenceRange}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Grid>
              )}

              {/* Supplement Recommendations */}
              {parsedData.labReport.supplements && parsedData.labReport.supplements.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, height: '100%', bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Info />
                      Supplement Recommendations
                    </Typography>
                    {parsedData.labReport.supplements.map((supplement, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {supplement.name}
                        </Typography>
                        {supplement.dosage && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Dosage:</strong> {supplement.dosage}
                          </Typography>
                        )}
                        {supplement.timing && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Timing:</strong> {supplement.timing}
                          </Typography>
                        )}
                        {supplement.justification && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                            {supplement.justification}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              )}

              {/* Lifestyle Recommendations */}
              {parsedData.labReport.lifestyle && parsedData.labReport.lifestyle.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 3, height: '100%', bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Warning />
                      Lifestyle Recommendations
                    </Typography>
                    <List dense>
                      {parsedData.labReport.lifestyle.map((lifestyle, index) => (
                        <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <CheckCircle color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                {lifestyle}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              )}

              {/* Follow-up */}
              {parsedData.labReport.followUp && (
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 3, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule />
                      Follow-up Plan
                    </Typography>

                    {parsedData.labReport.followUp.labsToRepeat && parsedData.labReport.followUp.labsToRepeat.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Labs to Repeat
                        </Typography>
                        {parsedData.labReport.followUp.labsToRepeat.map((lab, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {lab.name}
                            </Typography>
                            {lab.when && (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>When:</strong> {lab.when}
                              </Typography>
                            )}
                            {lab.why && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Why:</strong> {lab.why}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {parsedData.labReport.followUp.additionalTests && parsedData.labReport.followUp.additionalTests.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Additional Tests to Consider
                        </Typography>
                        {parsedData.labReport.followUp.additionalTests.map((test, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {test.name}
                            </Typography>
                            {test.reason && (
                              <Typography variant="body2" color="text.secondary">
                                {test.reason}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {parsedData.labReport.followUp.specialistReferrals && parsedData.labReport.followUp.specialistReferrals.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Specialist Referrals
                        </Typography>
                        {parsedData.labReport.followUp.specialistReferrals.map((referral, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {referral.type}
                            </Typography>
                            {referral.reason && (
                              <Typography variant="body2" color="text.secondary">
                                {referral.reason}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}
            </>
          )}

          {/* Raw Report Content - Fallback if no structured data */}
          {!hasStructuredData && recommendations.recommendations ? (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3, bgcolor: 'primary.50', border: 1, borderColor: 'grey.200' }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assessment />
                  Lab Report Analysis
                </Typography>
                <Box
                  sx={{
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 1,
                    boxShadow: 1,
                    border: 1,
                    borderColor: 'grey.200',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.7,
                      wordBreak: 'break-word',
                      margin: 0,
                      fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {(() => {
                      const data = recommendations.recommendations
                      if (typeof data === 'string') {
                        // Clean up the string for better readability
                        return data
                          .replace(/^\s*[{["']*(.*?)[}\]"']*\s*$/s, '$1') // Remove wrapping quotes/brackets
                          .replace(/\\n/g, '\n') // Convert escaped newlines
                          .replace(/\\"/g, '"') // Convert escaped quotes
                          .replace(/\s*\n\s*/g, '\n\n') // Add proper spacing between paragraphs
                          .trim()
                      } else if (typeof data === 'object' && data !== null) {
                        // Try to extract meaningful text from object
                        const obj = data as Record<string, unknown>
                        let result = ''

                        // Look for common text fields
                        const textFields = ['analysis', 'interpretation', 'summary', 'report', 'content', 'text', 'result', 'findings']
                        for (const field of textFields) {
                          if (typeof obj[field] === 'string' && obj[field]) {
                            result += obj[field] + '\n\n'
                          }
                        }

                        // If no text fields found, format as readable key-value pairs
                        if (!result) {
                          Object.entries(obj).forEach(([key, value]) => {
                            if (typeof value === 'string' || typeof value === 'number') {
                              const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
                              result += `${formattedKey}: ${value}\n\n`
                            }
                          })
                        }

                        return result.trim() || 'Report data available but format not recognized.'
                      }
                      return 'Unable to display report content.'
                    })()}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ) : null}

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Report Metadata - technical details are screen only, excluded from print/PDF export */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {recommendations.generatedAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Generated: {formatDate(recommendations.generatedAt)}
                  </Typography>
                </Box>
              )}

              {recommendations.metadata?.modelUsed && (
                <Chip
                  label={`AI Model: ${recommendations.metadata.modelUsed}`}
                  size="small"
                  variant="outlined"
                  color="default"
                  sx={{ displayPrint: 'none' }}
                />
              )}

              {recommendations.metadata?.processingTimeMs && (
                <Typography variant="caption" color="text.secondary" sx={{ displayPrint: 'none' }}>
                  Processing time: {recommendations.metadata.processingTimeMs}ms
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}